import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceService, {
    extractErrorMessage,
    SellerGroupMembers,
    SellerListing,
} from '../../../services/marketplaceService';
import { formatEuro } from '../../../pages/marketplaceVisibility';
import MaterialSymbol from '../../common/MaterialSymbol';
import { ListingStatusChip, VisibilityBadge } from './SellerBadges';
import ListingEditorDialog from './ListingEditorDialog';
import '../marketplace-design.css';

type Confirm = { kind: 'withdraw' | 'delete'; listing: SellerListing } | null;

const priceSummary = (l: SellerListing, translate: (k: string) => string): string => {
    const parts: string[] = [];
    if (l.allowReference) parts.push(`${translate('marketplaceSeller.listings.priceRef')} ${formatEuro(l.priceReference, l.currency)}`);
    if (l.allowCopy) parts.push(`${translate('marketplaceSeller.listings.priceCopy')} ${formatEuro(l.priceCopy, l.currency)}`);
    if (l.allowSeatCopy) parts.push(`${translate('marketplaceSeller.listings.priceSeat')} ${formatEuro(l.priceSeat, l.currency)}`);
    return parts.join(' · ') || '–';
};

/**
 * „Meine Angebote" (Plan 3.7): Tabelle mit Status, Sichtbarkeit, Preisen,
 * Verkäufen und den Lebenszyklus-Aktionen. Unter 900 px wird jede Zeile zur
 * Karte; Desktop bleibt die Tabelle. „Kurs anbieten“ im Seitenkopf öffnet den
 * Assistenten über `?new=1`.
 */
const SellerListingsPanel: React.FC<{ canManage?: boolean }> = () => {
    const translate = useTranslate();
    const notify = useNotify();

    const [listings, setListings] = useState<SellerListing[]>([]);
    const [group, setGroup] = useState({ hasGroup: false, memberCount: 0 });
    const [members, setMembers] = useState<SellerGroupMembers>({ hasGroup: false, members: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editor, setEditor] = useState<{ open: boolean; listing: SellerListing | null }>({ open: false, listing: null });
    const [confirm, setConfirm] = useState<Confirm>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [params, setParams] = useSearchParams();

    // „Kurs anbieten“ im Seitenkopf verweist mit ?new=1 hierher und öffnet den Assistenten
    useEffect(() => {
        if (params.get('new') === '1') {
            setEditor({ open: true, listing: null });
            setParams({}, { replace: true });
        }
    }, [params, setParams]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await marketplaceService.getSellerListings();
            setListings(result.listings);
            setGroup(result.group);
            if (result.group.hasGroup) {
                marketplaceService.getSellerGroupMembers().then(setMembers).catch(() => undefined);
            }
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceSeller.listings.loadError')));
        } finally {
            setLoading(false);
        }
    }, [translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const run = async (listing: SellerListing, fn: () => Promise<unknown>, successKey: string) => {
        setBusyId(listing.id);
        try {
            await fn();
            notify(translate(successKey), { type: 'success' });
            await load();
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceSeller.listings.actionError')), { type: 'error' });
        } finally {
            setBusyId(null);
        }
    };

    const onConfirm = async () => {
        if (!confirm) return;
        const { kind, listing } = confirm;
        setConfirm(null);
        await run(
            listing,
            () => (kind === 'withdraw' ? marketplaceService.withdrawSellerListing(listing.id) : marketplaceService.deleteSellerListing(listing.id)),
            kind === 'withdraw' ? 'marketplaceSeller.listings.withdrawn' : 'marketplaceSeller.listings.deleted',
        );
    };

    const actions = (l: SellerListing) => (
        <div className="mk-ico-act">
            {l.actions.includes('submit') && (
                <button
                    type="button"
                    className="mk-btn-s mk-sm"
                    disabled={busyId === l.id}
                    onClick={() => run(l, () => marketplaceService.submitSellerListing(l.id), l.visibility === 'group' ? 'marketplaceSeller.listings.published' : 'marketplaceSeller.listings.submitted')}
                >
                    {translate('marketplaceSeller.listings.submit')}
                </button>
            )}
            {(l.editMode === 'all' || l.editMode === 'content') && (
                <button
                    type="button"
                    disabled={busyId === l.id}
                    onClick={() => setEditor({ open: true, listing: l })}
                    aria-label={translate('marketplaceSeller.listings.edit')}
                    title={translate('marketplaceSeller.listings.edit')}
                >
                    <MaterialSymbol name="edit" />
                </button>
            )}
            {l.actions.includes('withdraw') && (
                <button
                    type="button"
                    disabled={busyId === l.id}
                    onClick={() => setConfirm({ kind: 'withdraw', listing: l })}
                    aria-label={translate('marketplaceSeller.listings.withdraw')}
                    title={translate('marketplaceSeller.listings.withdraw')}
                >
                    <MaterialSymbol name="pause" />
                </button>
            )}
            {l.actions.includes('delete') && (
                <button
                    type="button"
                    className="mk-danger"
                    disabled={busyId === l.id}
                    onClick={() => setConfirm({ kind: 'delete', listing: l })}
                    aria-label={translate('marketplaceSeller.listings.delete')}
                    title={translate('marketplaceSeller.listings.delete')}
                >
                    <MaterialSymbol name="delete" />
                </button>
            )}
        </div>
    );

    const notes = (l: SellerListing) => (
        <>
            {l.status === 'rejected' && l.rejectionReason && (
                <span className="note e">{translate('marketplaceSeller.listings.rejectedReason', { reason: l.rejectionReason })}</span>
            )}
            {l.status === 'in_review' && <span className="note">{translate('marketplaceSeller.listings.inReviewHint')}</span>}
            {l.warnings.length > 0 && l.status !== 'published' && (
                <span className="note w">{l.warnings.map((w) => translate(`marketplaceSeller.listings.warnings.${w}`)).join(' ')}</span>
            )}
        </>
    );

    return (
        <div>
            <div className="mk-sec-t">
                <div>
                    <h2>{translate('marketplaceSeller.listings.title')}</h2>
                    <p>{translate('marketplaceSeller.listings.intro')}</p>
                </div>
            </div>

            {error && (
                <div className="mk-alert err" role="alert">
                    <MaterialSymbol name="error" />
                    <div className="body">{error}</div>
                </div>
            )}

            {!loading && !error && listings.length === 0 && (
                <div className="mk-empty" role="status">
                    <MaterialSymbol name="storefront" />
                    <p>{translate('marketplaceSeller.listings.empty')}</p>
                </div>
            )}

            {listings.length > 0 && (
                <>
                    <div className="mk-tbl-wrap wide">
                        <table className="mk-tbl">
                            <thead>
                                <tr>
                                    <th>{translate('marketplaceSeller.listings.col.course')}</th>
                                    <th>{translate('marketplaceSeller.listings.col.visibility')}</th>
                                    <th>{translate('marketplaceSeller.listings.col.status')}</th>
                                    <th>{translate('marketplaceSeller.listings.col.prices')}</th>
                                    <th className="r">{translate('marketplaceSeller.listings.col.sales')}</th>
                                    <th className="r">{translate('marketplaceSeller.listings.col.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listings.map((l) => (
                                    <tr key={l.id} data-testid={`listing-row-${l.id}`}>
                                        <td style={{ maxWidth: 260 }}>
                                            <b>{l.title}</b>
                                            {l.isBundle && (
                                                <div>
                                                    <span className="mk-pill neu" data-testid={`bundle-pill-${l.id}`} title={(l.courses ?? []).map((c) => c.title).join(', ')}>
                                                        <MaterialSymbol name="layers" />
                                                        {translate('marketplaceBuyer.bundle.pill', { count: l.courseCount ?? l.courses?.length ?? 0 })}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td><VisibilityBadge visibility={l.visibility} short /></td>
                                        <td>
                                            <div className="mk-ocell">
                                                <ListingStatusChip status={l.status} />
                                                {notes(l)}
                                            </div>
                                        </td>
                                        <td className="mut" style={{ maxWidth: 280 }}>{priceSummary(l, translate)}</td>
                                        <td className="r">{l.totalLicenses}</td>
                                        <td>{actions(l)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mk-cards">
                        {listings.map((l) => (
                            <div key={l.id} className="mk-card" data-testid={`listing-card-${l.id}`}>
                                <div className="t">{l.title}</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0' }}>
                                    {l.isBundle && (
                                        <span className="mk-pill neu">
                                            <MaterialSymbol name="layers" />
                                            {translate('marketplaceBuyer.bundle.pill', { count: l.courseCount ?? l.courses?.length ?? 0 })}
                                        </span>
                                    )}
                                    <VisibilityBadge visibility={l.visibility} short />
                                    <ListingStatusChip status={l.status} />
                                </div>
                                <div className="mk-ocell">{notes(l)}</div>
                                <div className="m" style={{ margin: '8px 0' }}>{priceSummary(l, translate)}</div>
                                {actions(l)}
                            </div>
                        ))}
                    </div>
                </>
            )}

            <ListingEditorDialog
                open={editor.open}
                listing={editor.listing}
                hasGroup={group.hasGroup}
                groupMembers={members.members}
                groupTotal={members.total || Math.max(0, group.memberCount - 1)}
                takenCourseIds={listings
                    .filter((l) => l.id !== editor.listing?.id)
                    .flatMap((l) => (l.courses?.length ? l.courses.map((c) => c.id) : [l.course.id]))}
                onClose={() => setEditor({ open: false, listing: null })}
                onSaved={() => {
                    setEditor({ open: false, listing: null });
                    load();
                }}
            />

            <Dialog open={confirm !== null} onClose={() => setConfirm(null)} className="mk-wiz">
                <div className="mk-wh">
                    <DialogTitle sx={{ p: 0, font: 'inherit' }}>
                        <span style={{ fontSize: '1.35rem', fontWeight: 700 }}>
                            {translate(confirm?.kind === 'delete' ? 'marketplaceSeller.listings.confirmDeleteTitle' : 'marketplaceSeller.listings.confirmWithdrawTitle')}
                        </span>
                    </DialogTitle>
                </div>
                <DialogContent sx={{ pt: 1 }}>
                    <DialogContentText sx={{ color: 'var(--oa-text-2)' }}>
                        {translate(confirm?.kind === 'delete' ? 'marketplaceSeller.listings.confirmDelete' : 'marketplaceSeller.listings.confirmWithdraw', {
                            title: confirm?.listing.title ?? '',
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions className="mk-wf" sx={{ justifyContent: 'flex-end' }}>
                    <button type="button" className="mk-btn-text" onClick={() => setConfirm(null)}>{translate('ra.action.cancel')}</button>
                    <button type="button" className={`mk-btn-s mk-sm${confirm?.kind === 'delete' ? ' mk-danger' : ''}`} onClick={onConfirm}>
                        {translate(confirm?.kind === 'delete' ? 'marketplaceSeller.listings.delete' : 'marketplaceSeller.listings.withdraw')}
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default SellerListingsPanel;
